/**
 * SalesPage — myyntitapahtumat
 *
 * Lists POS/terminal sales (e.g. SumUp card payments) stored in the sales table.
 * Allows assigning an account code (tiliöinti) and viewing VAT breakdown.
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/auth'
import {
  PageHeader,
  FilterBar,
  SearchFilter,
  Table,
  Card,
  Badge,
  Modal,
  NoOrganizationMessage,
  formatEuro,
  Select,
} from '../components'
import { salesService, type SaleRow } from '../api/services/SalesService'

export default function SalesPage() {
  const { t } = useTranslation()

  const VAT_RATE_OPTIONS = [
    { value: '', label: t('sales.vatNotSet') },
    { value: '25.5', label: '25,5 %' },
    { value: '14', label: '14 %' },
    { value: '10', label: '10 %' },
    { value: '0', label: '0 %' },
  ]

  const ACCOUNT_CODE_OPTIONS = [
    { value: '', label: t('sales.notAccounted') },
    { value: '3000', label: '3000 Myyntitulot' },
    { value: '3100', label: '3100 Kilpailumaksut' },
    { value: '3200', label: '3200 Jäsenmaksut' },
    { value: '3300', label: '3300 Lahjoitukset' },
    { value: '3400', label: '3400 Avustukset' },
    { value: '3500', label: '3500 Muut tulot' },
  ]
  const { activeOrg } = useAuthStore()

  const [sales, setSales] = useState<SaleRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Categorization modal
  const [editingSale, setEditingSale] = useState<SaleRow | null>(null)
  const [editAccountCode, setEditAccountCode] = useState('')
  const [editVatRate, setEditVatRate] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!activeOrg) return
    loadSales()
  }, [activeOrg, startDate, endDate])

  const loadSales = async () => {
    if (!activeOrg) return
    setLoading(true)
    try {
      const resp = await salesService.list({
        organizationId: activeOrg.id,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: 500,
      })
      setSales(resp.sales || [])
      setError('')
    } catch {
      setError('Myyntitapahtumien lataus epäonnistui')
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    if (!search) return sales
    const q = search.toLowerCase()
    return sales.filter(s =>
      s.description?.toLowerCase().includes(q) ||
      s.payment_type?.toLowerCase().includes(q) ||
      s.account_code?.toLowerCase().includes(q) ||
      s.external_id?.toLowerCase().includes(q)
    )
  }, [sales, search])

  // Totals
  const totals = useMemo(() => ({
    gross: filtered.reduce((sum, s) => sum + s.amount, 0),
    vat: filtered.reduce((sum, s) => sum + (s.vat_amount ?? 0), 0),
    net: filtered.reduce((sum, s) => sum + (s.net_amount ?? s.amount), 0),
    fee: filtered.reduce((sum, s) => sum + (s.fee ?? 0), 0),
  }), [filtered])

  const openEdit = (sale: SaleRow) => {
    setEditingSale(sale)
    setEditAccountCode(sale.account_code ?? '')
    setEditVatRate(sale.vat_rate != null ? String(sale.vat_rate) : '')
  }

  const handleSave = async () => {
    if (!editingSale || !activeOrg) return
    setSaving(true)
    try {
      await salesService.update(editingSale.id, activeOrg.id, {
        accountCode: editAccountCode || undefined,
        vatRate: editVatRate !== '' ? Number(editVatRate) : undefined,
      })
      setSales(prev => prev.map(s =>
        s.id === editingSale.id
          ? { ...s, account_code: editAccountCode || null, vat_rate: editVatRate !== '' ? Number(editVatRate) : null }
          : s
      ))
      setEditingSale(null)
    } catch {
      alert('Tallennus epäonnistui')
    } finally {
      setSaving(false)
    }
  }

  if (!activeOrg) return <NoOrganizationMessage />

  const columns = [
    {
      key: 'date',
      header: 'Päivämäärä',
      render: (s: SaleRow) => (
        <span className="text-sm text-gray-900">{s.date}</span>
      ),
    },
    {
      key: 'description',
      header: t('sales.description'),
      render: (s: SaleRow) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{s.description || '—'}</p>
          {s.payment_type && (
            <p className="text-xs text-gray-500">{s.payment_type}</p>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('sales.gross'),
      render: (s: SaleRow) => (
        <span className="text-sm font-medium text-green-700">{formatEuro(s.amount)}</span>
      ),
    },
    {
      key: 'vat',
      header: t('sales.vat'),
      render: (s: SaleRow) => (
        <div className="text-sm text-gray-700">
          {s.vat_amount != null ? (
            <span>{formatEuro(s.vat_amount)}{s.vat_rate != null ? ` (${s.vat_rate} %)` : ''}</span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'net',
      header: t('sales.net'),
      render: (s: SaleRow) => (
        <span className="text-sm text-gray-900">
          {s.net_amount != null ? formatEuro(s.net_amount) : formatEuro(s.amount)}
        </span>
      ),
    },
    {
      key: 'fee',
      header: t('sales.commission'),
      render: (s: SaleRow) => (
        <span className="text-sm text-red-600">
          {s.fee != null ? formatEuro(s.fee) : '—'}
        </span>
      ),
    },
    {
      key: 'account_code',
      header: t('sales.accounting'),
      render: (s: SaleRow) => (
        s.account_code ? (
          <Badge variant="neutral">{s.account_code}</Badge>
        ) : (
          <span className="text-xs text-gray-400">{t('sales.notAccounted')}</span>
        )
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (s: SaleRow) => (
        <button
          onClick={() => openEdit(s)}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {t('sales.assignAccount')}
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sales.title')}
        subtitle={t('sales.transactionCount', { count: filtered.length })}
      />

      <FilterBar>
        <SearchFilter
          value={search}
          onChange={setSearch}
          placeholder={t('sales.searchByDescription')}
        />
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder={t('sales.dateFrom')}
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder={t('sales.dateTo')}
        />
      </FilterBar>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Yhteenveto */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t('sales.grossTotal'), value: formatEuro(totals.gross), color: 'text-green-700' },
          { label: t('sales.vatTotal'), value: formatEuro(totals.vat), color: 'text-gray-700' },
          { label: t('sales.netTotal'), value: formatEuro(totals.net), color: 'text-indigo-700' },
          { label: t('sales.commissionsTotal'), value: formatEuro(totals.fee), color: 'text-red-600' },
        ].map(item => (
          <Card key={item.label} className="p-4">
            <p className="text-xs text-gray-500 mb-1">{item.label}</p>
            <p className={`text-lg font-semibold ${item.color}`}>{item.value}</p>
          </Card>
        ))}
      </div>

      <Card>
        <Table
          columns={columns}
          data={filtered}
          keyExtractor={s => s.id}
          loading={loading}
          emptyMessage={t('sales.noSalesInPeriod')}
        />
      </Card>

      {/* Tiliöintimodaali */}
      {editingSale && (
        <Modal
          onClose={() => setEditingSale(null)}
          title={t('sales.categorizeSale')}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700">{editingSale.description || '—'}</p>
              <p className="text-xs text-gray-500">{editingSale.date} · {formatEuro(editingSale.amount)}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sales.account')}</label>
              <Select
                value={editAccountCode}
                onChange={e => setEditAccountCode(e.target.value)}
                options={ACCOUNT_CODE_OPTIONS}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sales.vatRate')}</label>
              <Select
                value={editVatRate}
                onChange={e => setEditVatRate(e.target.value)}
                options={VAT_RATE_OPTIONS}
              />
              {editingSale.vat_amount != null && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('sales.sumupVatNote', { amount: formatEuro(editingSale.vat_amount) })}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => setEditingSale(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
